import React, { useCallback, useEffect, useState } from 'react';
import { API } from 'utilities/api';
import useSiteTheme from './useSiteTheme';
import { SiteHeader, SiteFooter } from './SiteFrame';

/**
 * The booking form a customer actually uses, on the brand's own domain.
 *
 * Plain elements and inline styles, like the rest of the public site: this
 * renders on the brand's domain, outside any stylesheet of ours, and must not
 * depend on the dashboard's theme.
 *
 * Three steps on one screen rather than a wizard — pick the thing, pick the
 * time, say who you are. A booking is a two-minute errand and a wizard makes
 * it feel like an application form; the whole state fits on one screen at
 * phone width if the slots are a wrapping grid rather than a list.
 */

const todayKey = () => new Date().toISOString().slice(0, 10);

const shiftDay = (key, days) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

const clockOf = (iso, timeZone) => new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timeZone || undefined,
}).format(new Date(iso));

const longDate = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long',
  }).format(new Date(Date.UTC(y, m - 1, d)));
};

export default function BookingPage() {
  const theme = useSiteTheme();
  const [frame, setFrame] = useState(null);
  const [resources, setResources] = useState([]);
  const [resourceId, setResourceId] = useState('');
  const [date, setDate] = useState(todayKey);
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', customer_phone: '', party_size: 2, note: '' });
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.publicSiteFrame({}).then(setFrame).catch(() => setFrame(null));
    API.publicBookableResources()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setResources(list);
        if (list.length > 0) setResourceId(String(list[0]._id));
      })
      .catch(() => setResources([]));
  }, []);

  const loadDay = useCallback(() => {
    if (!resourceId) return;
    setSlot(null);
    API.publicBookingDay({ resource_id: resourceId, date })
      .then(setDay)
      .catch(() => setDay(null));
  }, [resourceId, date]);

  useEffect(() => { loadDay(); }, [loadDay]);

  const resource = resources.find((r) => String(r._id) === String(resourceId));
  const ground = theme.isDark ? '#0f1115' : '#ffffff';
  const ink = theme.isDark ? '#e5e7eb' : '#0f172a';
  const muted = theme.isDark ? 'rgba(229,231,235,0.65)' : '#64748b';
  const line = theme.isDark ? 'rgba(255,255,255,0.14)' : '#e2e8f0';
  const accent = frame?.brand?.brand_color || '#4F46E5';

  const submit = (e) => {
    e.preventDefault();
    if (!slot) { setError('Pick a time first.'); return; }
    if (!form.customer_name.trim()) { setError('We need a name for the booking.'); return; }
    setBusy(true);
    setError('');
    API.publicBook({
      resource_id: resourceId,
      starts_at: slot.starts_at,
      party_size: Number(form.party_size) || 1,
      customer_name: form.customer_name,
      customer_email: form.customer_email,
      customer_phone: form.customer_phone,
      note: form.note,
    })
      .then((res) => setDone(res))
      // The server's refusals are written for the person reading them —
      // "Only 1 left at that time" — so they are shown, not replaced.
      .catch((err) => { setError(err?.message || 'That did not go through.'); loadDay(); })
      .finally(() => setBusy(false));
  };

  const field = {
    width: '100%', padding: '10px 12px', fontSize: 15, borderRadius: 8,
    border: `1px solid ${line}`, background: theme.isDark ? 'rgba(255,255,255,0.04)' : '#fff',
    color: ink, fontFamily: 'inherit', boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: ground, color: ink }}>
      <SiteHeader header={frame?.header} brandName={frame?.brand?.name} brandLogo={frame?.brand?.logo_upload_path} theme={theme} />

      <div style={{ flex: 1, width: '100%', maxWidth: 680, margin: '0 auto', padding: '32px 16px 64px' }}>
        {done ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <h1 style={{ fontSize: 'clamp(24px, 4vw, 32px)', margin: '0 0 12px' }}>
              {done.status === 'pending' ? 'Request sent' : 'You are booked in'}
            </h1>
            <p style={{ color: muted, lineHeight: 1.6, margin: '0 0 8px' }}>
              {longDate(date)} at {clockOf(done.starts_at, resource?.timezone)} for {done.party_size}.
            </p>
            <p style={{ color: muted, lineHeight: 1.6, margin: 0 }}>
              {done.status === 'pending'
                ? 'We will confirm it shortly.'
                : 'Quote this if you need to change it:'}
              {' '}
              <strong style={{ color: ink }}>{done.reference}</strong>
            </p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h1 style={{ fontSize: 'clamp(26px, 4.5vw, 36px)', margin: '0 0 24px' }}>Book a table</h1>

            {resources.length === 0 && (
              <p style={{ color: muted }}>There is nothing to book here at the moment.</p>
            )}

            {resources.length > 1 && (
              <label style={{ display: 'block', marginBottom: 20 }}>
                <span style={{ display: 'block', fontSize: 13, color: muted, marginBottom: 6 }}>What</span>
                <select
                  value={resourceId}
                  onChange={(e) => setResourceId(e.target.value)}
                  style={field}
                >
                  {resources.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
                </select>
              </label>
            )}

            <div style={{ marginBottom: 20 }}>
              <span style={{ display: 'block', fontSize: 13, color: muted, marginBottom: 6 }}>When</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type='button' onClick={() => setDate((d) => shiftDay(d, -1))}
                  style={{ ...field, width: 44, cursor: 'pointer' }} aria-label='Previous day'>‹</button>
                <input type='date' value={date} onChange={(e) => setDate(e.target.value || todayKey())}
                  style={{ ...field, width: 'auto', flex: 1, minWidth: 150 }} />
                <button type='button' onClick={() => setDate((d) => shiftDay(d, 1))}
                  style={{ ...field, width: 44, cursor: 'pointer' }} aria-label='Next day'>›</button>
              </div>
            </div>

            {/* A wrapping grid, not a list: twelve slots as a column is a page
                of scrolling on a phone for a decision that is one glance. */}
            <div style={{ marginBottom: 24 }}>
              {day?.closed || (day && day.slots.length === 0) ? (
                <p style={{ color: muted, margin: 0 }}>Closed on {longDate(date)}.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: 8 }}>
                  {(day?.slots || []).map((s) => {
                    const chosen = slot && slot.starts_at === s.starts_at;
                    return (
                      <button
                        key={s.starts_at}
                        type='button'
                        disabled={!s.available}
                        onClick={() => { setSlot(s); setError(''); }}
                        title={s.reason === 'too_soon' ? 'Too soon to book online' : s.reason === 'full' ? 'Fully booked' : ''}
                        style={{
                          padding: '10px 6px', fontSize: 15, borderRadius: 8, cursor: s.available ? 'pointer' : 'not-allowed',
                          border: `1px solid ${chosen ? accent : line}`,
                          background: chosen ? accent : (theme.isDark ? 'rgba(255,255,255,0.04)' : '#fff'),
                          color: chosen ? '#fff' : (s.available ? ink : muted),
                          opacity: s.available ? 1 : 0.45,
                          fontFamily: 'inherit',
                        }}
                      >
                        {clockOf(s.starts_at, resource?.timezone)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gap: 12, marginBottom: 20 }}>
              <label>
                <span style={{ display: 'block', fontSize: 13, color: muted, marginBottom: 6 }}>How many</span>
                <input type='number' min={1} value={form.party_size}
                  onChange={(e) => setForm({ ...form, party_size: e.target.value })} style={field} />
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 13, color: muted, marginBottom: 6 }}>Name</span>
                <input value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })} style={field} />
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 13, color: muted, marginBottom: 6 }}>Phone</span>
                <input value={form.customer_phone}
                  onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} style={field} />
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 13, color: muted, marginBottom: 6 }}>Email</span>
                <input type='email' value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })} style={field} />
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 13, color: muted, marginBottom: 6 }}>Anything we should know</span>
                <input value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })} style={field}
                  placeholder='Allergies, a high chair, a quiet table' />
              </label>
            </div>

            {error && (
              <p style={{ color: '#dc2626', margin: '0 0 14px', lineHeight: 1.5 }}>{error}</p>
            )}

            <button
              type='submit'
              disabled={busy || !slot}
              style={{
                width: '100%', padding: '14px 18px', fontSize: 16, fontWeight: 600,
                borderRadius: 10, border: 'none', cursor: busy || !slot ? 'not-allowed' : 'pointer',
                background: accent, color: '#fff', opacity: busy || !slot ? 0.6 : 1,
                fontFamily: 'inherit',
              }}
            >
              {busy ? 'Just a moment…' : slot
                ? `Book ${clockOf(slot.starts_at, resource?.timezone)} on ${longDate(date)}`
                : 'Pick a time above'}
            </button>
          </form>
        )}
      </div>

      <SiteFooter footer={frame?.footer} brandName={frame?.brand?.name} theme={theme} />
    </div>
  );
}
